<template>
  <div>
    <v-data-table
      :loading="loadingTable"
      :headers="headers"
      :items="channels"
      :page-count="numberOfPagesPaginate"
      :page="currentPagePaginate"
      :server-items-length="totalItems"
      :options.sync="options"
      class="elevation-1"
      :footer-props="{ itemsPerPageText: 'Itens por página', itemsPerPageOptions: [5, 10, 15], showFirstLastPage: true }"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Canais de Reserva</v-toolbar-title>
          <v-spacer />
          <v-btn
            color="primary"
            dark
            rounded
            @click="newItem"
          >
            <v-icon
              small
              class="mr-1"
            >
              {{ icons.mdiPlus }}
            </v-icon>
            Novo Canal
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:item.is_active="{ item }">
        <v-chip
          small
          :color="item.is_active ? 'success' : 'secondary'"
          dark
        >
          {{ item.is_active ? 'Ativo' : 'Desativado' }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon
          color="primary"
          class="mr-3"
          @click="editItem(item)"
        >
          {{ icons.mdiPencilOutline }}
        </v-icon>
        <v-icon
          v-if="item.is_active"
          color="primary"
          class="mr-3"
          title="Vincular reservas antigas"
          @click="openLegacyDialog(item)"
        >
          {{ icons.mdiLinkVariant }}
        </v-icon>
        <v-icon
          v-if="item.is_active"
          color="error"
          @click="confirmDeactivate(item)"
        >
          {{ icons.mdiDeleteOutline }}
        </v-icon>
      </template>

      <template v-slot:no-data>
        <NoDataTable />
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog"
      max-width="500px"
    >
      <v-card>
        <v-card-title>{{ formTitle }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editedItem.name"
            outlined
            label="Canal"
            :error-messages="errors.name"
            @input="errors.name = []"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="close"
          >
            Fechar
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            @click="save"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogLegacy"
      max-width="600px"
    >
      <v-card>
        <v-card-title>Vincular Reservas Antigas</v-card-title>
        <v-card-text>
          <p class="mb-4">
            Canal: <strong>{{ legacyChannel.name }}</strong>
          </p>
          <v-select
            v-model="selectedLegacyOrigins"
            :items="legacyOrigins"
            :loading="loadingLegacy"
            item-text="label"
            label="Origens sem canal vinculado"
            multiple
            outlined
            return-object
            :disabled="loadingLegacy"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.label }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.total }} reserva(s)</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-select>
          <p class="text--secondary mb-0">
            Somente reservas sem canal vinculado serão associadas ao canal selecionado.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="loadingLegacy"
            @click="closeLegacyDialog"
          >
            Fechar
          </v-btn>
          <v-btn
            color="primary"
            :disabled="selectedLegacyOrigins.length === 0"
            :loading="loadingLegacy"
            @click="assignLegacyReservesToChannel"
          >
            Vincular
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <MsgDelete
      v-model="dialogDeactivate"
      message="Deseja realmente desativar este canal? O histórico será mantido."
      @delete-yes="deactivateItem"
    />
  </div>
</template>

<script>
import {
  mdiDeleteOutline, mdiLinkVariant, mdiPencilOutline, mdiPlus,
} from '@mdi/js'
import { mapActions } from 'vuex'
import MsgDelete from '@/components/MsgDelete.vue'
import NoDataTable from '@/components/NoDataTable.vue'

export default {
  components: { MsgDelete, NoDataTable },
  data: () => ({
    channels: [],
    options: {},
    totalItems: 0,
    numberOfPagesPaginate: 0,
    currentPagePaginate: 0,
    loadingTable: false,
    loading: false,
    dialog: false,
    dialogDeactivate: false,
    dialogLegacy: false,
    editedIndex: -1,
    editedItem: { name: '' },
    legacyChannel: { name: '' },
    legacyOrigins: [],
    selectedLegacyOrigins: [],
    loadingLegacy: false,
    errors: { name: [] },
    headers: [
      { text: 'Canal', value: 'name' },
      { text: 'Status', value: 'is_active' },
      {
        text: '', value: 'actions', sortable: false, align: 'end',
      },
    ],
    icons: {
      mdiDeleteOutline, mdiLinkVariant, mdiPencilOutline, mdiPlus,
    },
  }),
  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'Novo Canal de Reserva' : 'Editar Canal de Reserva'
    },
  },
  watch: {
    options: {
      handler() { this.initialize() },
    },
    dialog(value) { if (!value) this.close() },
    dialogLegacy(value) { if (!value) this.closeLegacyDialog() },
  },
  methods: {
    ...mapActions('reserve_channel', [
      'getAll', 'getLegacyOrigins', 'store', 'update', 'deactivate', 'assignLegacyReserves',
    ]),
    initialize() {
      this.loadingTable = true
      const { page, itemsPerPage } = this.options
      this.getAll({ page, itemsPerPage: itemsPerPage || 10 })
        .then(response => {
          this.channels = response.data.data
          this.totalItems = response.data.total
          this.numberOfPagesPaginate = response.data.last_page
          this.currentPagePaginate = response.data.current_page
        })
        .finally(() => { this.loadingTable = false })
    },
    newItem() {
      this.editedIndex = -1
      this.editedItem = { name: '' }
      this.errors = { name: [] }
      this.dialog = true
    },
    editItem(item) {
      this.editedIndex = this.channels.indexOf(item)
      this.editedItem = { ...item }
      this.errors = { name: [] }
      this.dialog = true
    },
    close() {
      this.dialog = false
      this.editedIndex = -1
      this.editedItem = { name: '' }
      this.errors = { name: [] }
    },
    save() {
      this.loading = true
      const action = this.editedIndex === -1 ? this.store : this.update
      action(this.editedItem)
        .then(response => {
          if (this.editedIndex === -1) this.channels.push(response.data)
          else Object.assign(this.channels[this.editedIndex], response.data)
          this.$toast.success('Salvo com sucesso!')
          this.close()
        })
        .catch(error => { this.errors = error.response?.data?.errors || { name: [] } })
        .finally(() => { this.loading = false })
    },
    confirmDeactivate(item) {
      this.editedItem = item
      this.dialogDeactivate = true
    },
    deactivateItem() {
      this.deactivate(this.editedItem)
        .then(response => {
          Object.assign(this.editedItem, response.data)
          this.$toast.success('Canal desativado com sucesso!')
          this.dialogDeactivate = false
        })
    },
    openLegacyDialog(channel) {
      this.legacyChannel = channel
      this.selectedLegacyOrigins = []
      this.dialogLegacy = true
      this.loadingLegacy = true

      this.getLegacyOrigins()
        .then(response => { this.legacyOrigins = response.data })
        .catch(() => { this.$toast.error('Não foi possível carregar as reservas antigas.') })
        .finally(() => { this.loadingLegacy = false })
    },
    closeLegacyDialog() {
      this.dialogLegacy = false
      this.legacyChannel = { name: '' }
      this.selectedLegacyOrigins = []
    },
    assignLegacyReservesToChannel() {
      this.loadingLegacy = true

      this.assignLegacyReserves({
        channel: this.legacyChannel,
        origins: this.selectedLegacyOrigins.map(item => item.origin),
      })
        .then(response => {
          this.$toast.success(`${response.data.updated_count} reserva(s) vinculada(s) ao canal.`)
          this.closeLegacyDialog()
        })
        .catch(error => {
          this.$toast.error(error.response?.data?.message || 'Não foi possível vincular as reservas.')
        })
        .finally(() => { this.loadingLegacy = false })
    },
  },
}
</script>
